// Shared helpers for computing Presales Progress step statuses. Used by the
// Scope Actions tab (large donut) and the Operational Overview table (compact donut).

import { PRESALES_ACTIONS, PRESALES_STEPS } from '../types'
import type { PresalesActionItem, PresalesStepDef } from '../types'

export type StepState = 'done' | 'pending' | 'na'

export interface StepStatus {
  state: StepState
  completedAt: string | null
  /** Extra context for the tooltip, e.g. "Sandbox Access Sent selected". */
  detail: string
  /** True when this step is driven by a presalesAction (i.e. toggleable). */
  toggleable: boolean
  /** When a step has one unambiguous action id, this is it — used for one-click toggle. */
  singleActionId: string | null
  /**
   * Short abbreviation to render on the slice (e.g. "S" when Sandbox is the
   * active alternative, "U" when UAT is). Used for multi-action steps so the
   * user can see which alternative is selected without opening a tooltip.
   */
  abbrev?: string | null
}

/** Compute a StepStatus for each entry in PRESALES_STEPS. */
export function computeStepStatuses(
  actions: ReadonlyArray<PresalesActionItem> = [],
  scopeStatusDates: Record<string, string | null> = {},
  blueprintPercent = 0,
): StepStatus[] {
  const byId = new Map(actions.map((a) => [a.id, a]))

  const computeOne = (def: PresalesStepDef): StepStatus => {
    if (def.source.kind === 'blueprint') {
      return {
        state: blueprintPercent > 0 ? 'done' : 'pending',
        completedAt: null,
        detail: blueprintPercent > 0 ? `${blueprintPercent}% complete` : '',
        toggleable: false,
        singleActionId: null,
      }
    }
    if (def.source.kind === 'status') {
      const dt = scopeStatusDates[def.source.statusId]
      return {
        state: dt ? 'done' : 'pending',
        completedAt: dt ?? null,
        detail: '',
        toggleable: false,
        singleActionId: null,
      }
    }

    const matches = def.source.actionIds.map((id) => byId.get(id)).filter(Boolean) as PresalesActionItem[]
    const completed = matches.find((a) => a.completed)
    const toggleable = true
    const singleActionId = def.source.actionIds.length === 1 ? def.source.actionIds[0] : null

    if (completed) {
      let detail = ''
      let abbrev: string | null = null
      if (def.source.actionIds.length > 1) {
        const pickedDef = PRESALES_ACTIONS.find((d) => d.id === completed.id)
        if (pickedDef) {
          detail = `${pickedDef.label} selected`
          // First letter of the label (e.g. "Sandbox Access Sent" → "S")
          abbrev = pickedDef.label.trim().charAt(0).toUpperCase() || null
        }
      }
      return {
        state: 'done',
        completedAt: completed.completedAt,
        detail,
        toggleable,
        singleActionId,
        abbrev,
      }
    }

    const allNA = matches.length > 0 && matches.every((a) => a.notRequired)
    if (allNA) {
      return { state: 'na', completedAt: null, detail: 'Marked Not Required', toggleable, singleActionId }
    }

    return { state: 'pending', completedAt: null, detail: '', toggleable, singleActionId }
  }

  return PRESALES_STEPS.map(computeOne)
}

/**
 * Compact form used by surfaces that only have action booleans (like the Operational
 * Overview dashboard deal row). Converts a flat map of action-id → boolean into the
 * PresalesActionItem[] shape computeStepStatuses expects.
 */
export function buildActionItemsFromBooleans(flags: Record<string, boolean | number | null | undefined>): PresalesActionItem[] {
  return Object.entries(flags)
    .filter(([, v]) => v != null)
    .map(([id, v]) => ({
      id,
      completed: !!v,
      completedAt: null,
      notRequired: false,
      notes: '',
    }))
}
